import type {LabeledIdentified, NamedLabeledIdentified, Price, Rec} from 'src/typings';
import propFilter from '../filters/prop';
import inlineFilter from '../filters/inline';
import charactersFilter from '../filters/characters';
import locFilter from '../filters/loc';
import strParamsFilter from '../filters/str-params';
import recordDetailPathFilter from '../filters/record-detail-path.filter';
import {exists, isNullOrUndefined} from './custom-utils';

// extends BasicNamedLabeledIdentified<any> is necessary in order to not throw error when used
// because TS can not infer T from returned function params
// if it needs to be used with other props, update extend statement to include wanted props
export function prop<T extends NamedLabeledIdentified<any> | LabeledIdentified<any>>(propertyName: keyof T): (_: T[]) => Array<T[keyof T]> {
    return (array: T[]) => propFilter()(array, propertyName);
}

export function inline(delimiter?: string, prefix?: string, suffix?: string): (_: any[]) => string {
    return (array: any[]) => inlineFilter()(array, delimiter, prefix, suffix);
}

export function characters(maxChars: number, canBreakOnWord = false): (_: string) => string {
    return (input: string) => charactersFilter()(input, maxChars, canBreakOnWord);
}

export function loc(): (_: any) => string | null {
    return (object: any) => locFilter()(object);
}

export function strParams(...params: string[]): (_: string) => string {
    return (text: string) => strParamsFilter()(text, ...params);
}

export function recordDetailPath(): (_: Rec) => string {
    return (record) => recordDetailPathFilter()(record);
}

export function encodeUri(): (_: string) => string {
    return (value) => encodeURIComponent(value);
}

export function priceFormatter(locale: string): (_: Price) => string {
    return (price: Price) => {
        if (isNullOrUndefined(price)) {
            return '';
        }
        const formatter = new Intl.NumberFormat(locale, {style: 'currency', currency: price.currency.id});
        return formatter.format(price.amount);
    }
}

export function byteFormatter(formattingPrecision = 0): (bytes: number | null, precision?: number) => string {
    return (bytes, precision = formattingPrecision) => {
        if (!exists(bytes)) return '';
        if (isNaN(bytes) || !isFinite(bytes) || bytes < 0) return '-';
        if (bytes === 0) return '0B';

        const units = ['B', 'kB', 'MB', 'GB', 'TB', 'PB'];
        const magnitude = Math.floor(Math.log(bytes) / Math.log(1024));
        return (bytes / Math.pow(1024, Math.floor(magnitude))).toFixed(precision) + units[magnitude];
    };
}